/**
 * S3 API - Manejo de archivos en S3
 */

import { API_BASE_URL, DEFAULT_HEADERS } from '../config';
import { logger } from '../logger';

function parsePresignPayload(data) {
  if (data?.body && typeof data.body === 'string') {
    try {
      return JSON.parse(data.body);
    } catch {
      return data;
    }
  }
  return data;
}

async function parseErrorMessage(response, fallback) {
  try {
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const errorData = await response.json();
      const nested = parsePresignPayload(errorData);
      return nested.message || nested.error || errorData.message || errorData.error || fallback;
    }
    const text = await response.text();
    return text || fallback;
  } catch {
    return response.statusText || fallback;
  }
}

/**
 * Uploads a file to S3 via presigned URL (POST /s3/presign + PUT to S3).
 * Avoids API Gateway/Lambda payload limits from base64 POST /s3/upload.
 * @param {File} file - The file object to upload
 * @param {string} fileName - Optional custom filename (S3 key path)
 * @returns {Promise<Object>} - { body: string } with JSON { key, url, fileName }
 */
export async function uploadFileToS3(file, fileName = null) {
  try {
    logger.important(`API Call - uploadFileToS3: ${file.name}`);

    if (!file) {
      throw new Error('Se requiere un archivo válido');
    }

    const finalFileName = fileName || file.name;
    const presignBody = { fileName: finalFileName };
    if (file.type) presignBody.contentType = file.type;

    logger.info(`Solicitando presign para ${finalFileName}`);

    const presignResponse = await fetch(`${API_BASE_URL}/s3/presign`, {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify(presignBody),
    });

    if (!presignResponse.ok) {
      const errorMessage = await parseErrorMessage(
        presignResponse,
        'Error al obtener URL de subida'
      );
      throw new Error(errorMessage);
    }

    const presignData = parsePresignPayload(await presignResponse.json());
    const uploadUrl = presignData.uploadUrl || presignData.upload_url;
    const key = presignData.key ?? finalFileName;
    const contentType = presignData.contentType || file.type || 'application/octet-stream';

    if (!uploadUrl) {
      throw new Error('La API no devolvió URL de subida (uploadUrl)');
    }

    const putResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });

    if (!putResponse.ok) {
      throw new Error(
        `Error al subir archivo a S3: ${putResponse.status} ${putResponse.statusText}`
      );
    }

    const result = {
      body: JSON.stringify({
        key,
        url: presignData.url || '',
        fileName: key,
        fileSize: file.size,
        contentType,
      }),
    };

    logger.info('Archivo subido exitosamente:', key);
    return result;
  } catch (error) {
    logger.error('Error uploading file to S3:', error);
    throw error;
  }
}

// Mantener compatibilidad con código existente
export const uploadPdfToS3 = uploadFileToS3;

/**
 * Downloads a file from S3
 * @param {string} fileName - Name of the file to download
 * @returns {Promise<void>} - Triggers browser download
 */
export async function downloadFileFromS3(fileName) {
  try {
    if (!fileName) {
      throw new Error('Se requiere un nombre de archivo válido');
    }
    
    logger.important(`API Call - downloadFileFromS3: ${fileName}`);
    
    // Encode fileName for query parameter
    const encodedFileName = encodeURIComponent(fileName);
    const endpoint = `/s3/download?fileName=${encodedFileName}`;
    
    // Usar un timeout para evitar que la solicitud se quede colgada
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 segundos de timeout
    
    try {
      // Use fetch directly with fileName in query parameter
      // Try POST first, but also try with body if needed
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...DEFAULT_HEADERS,
          'Accept': '*/*', // Aceptar cualquier tipo de contenido para asegurar que la respuesta no sea filtrada
        },
        body: JSON.stringify({ key: [fileName] }), // Keep body for backward compatibility
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        // Intentar obtener detalles del error
        let errorMessage = 'Error al descargar archivo desde S3';
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
          } else {
            // Si no es JSON, intentar leer como texto
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          }
        } catch (e) {
          // Si no podemos parsear la respuesta, usar el status text
          errorMessage = response.statusText || errorMessage;
        }
        
        // Si es un error 403 con "Missing Authentication Token", podría ser que el endpoint necesite GET
        if (response.status === 403 && errorMessage.includes('Missing Authentication Token')) {
          logger.info('Intentando descarga con método GET...');
          // Intentar con GET
          const getResponse = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'GET',
            headers: {
              ...DEFAULT_HEADERS,
              'Accept': '*/*',
            },
            signal: controller.signal
          });
          
          if (getResponse.ok) {
            // Continuar con el procesamiento de la respuesta GET
            const blob = await getResponse.blob();
            if (blob.size === 0) {
              throw new Error('Archivo vacío o no disponible');
            }
            
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            
            setTimeout(() => {
              window.URL.revokeObjectURL(downloadUrl);
              document.body.removeChild(a);
            }, 0);
            
            logger.info('Archivo descargado exitosamente con GET:', fileName);
            return true;
          }
        }
        
        alert(`No se pudo descargar el archivo: ${errorMessage}`);
        throw new Error(errorMessage);
      }
      
      // Primero intentar obtener la URL directamente de la respuesta JSON
      try {
        const contentType = response.headers.get('content-type');
        
        // Si la respuesta es JSON, podría contener una URL directa
        if (contentType && contentType.includes('application/json')) {
          const jsonResponse = await response.json();
          
          // Buscar la URL en varias ubicaciones posibles de la respuesta
          let downloadUrl = null;
          
          if (jsonResponse.url) {
            downloadUrl = jsonResponse.url;
          } else if (jsonResponse.body) {
            try {
              // A veces el body es un string JSON
              const parsedBody = typeof jsonResponse.body === 'string' 
                ? JSON.parse(jsonResponse.body) 
                : jsonResponse.body;
                
              if (parsedBody.url) {
                downloadUrl = parsedBody.url;
              }
            } catch (e) {
              // Si no podemos parsear el body, continuar con el método del blob
            }
          }
          
          if (downloadUrl) {
            // Abrir la URL en una nueva pestaña o usar un iframe para descargar
            window.open(downloadUrl, '_blank');
            
            logger.info('Archivo descargado exitosamente mediante URL directa:', fileName);
            return true;
          }
        }
      } catch (jsonError) {
        // Continuar con el método de blob si hay un error procesando JSON
      }
      
      // Si no se pudo obtener una URL directa, proceder con el método del blob
      
      // Get the file content as blob
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Archivo vacío o no disponible');
      }
      
      // Create a download link and click it
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      }, 0);
      
      logger.info('Archivo descargado exitosamente:', fileName);
      
      return true;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError') {
        alert('La descarga ha tardado demasiado tiempo y se ha cancelado. Intente nuevamente.');
      }
      
      throw fetchError;
    }
  } catch (error) {
    logger.error('Error downloading file from S3:', error);
    alert(`Error al descargar el archivo: ${error.message}`);
    throw error;
  }
}

/**
 * Downloads multiple files from S3
 * @param {Array} fileNames - Array of file names to download
 * @returns {Promise<Object>} - Response with success status
 */
export async function downloadMultipleFilesFromS3(fileNames) {
  try {
    if (!fileNames || !Array.isArray(fileNames) || fileNames.length === 0) {
      throw new Error('Se requiere al menos un nombre de archivo válido');
    }
    
    logger.important(`API Call - downloadMultipleFilesFromS3: ${fileNames.length} files`);
    
    // Format the request body according to the API requirements
    const requestBody = {
      key: fileNames
    };
    
    // For multiple files, we might need to pass them differently
    // Encode file names for query parameter (comma-separated)
    const encodedFileNames = fileNames.map(f => encodeURIComponent(f)).join(',');
    const endpoint = `/s3/download?fileName=${encodedFileNames}`;
    
    // Usar un timeout para evitar que la solicitud se quede colgada
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 segundos de timeout para múltiples archivos
    
    try {
      // Use fetch with fileName in query parameter
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          ...DEFAULT_HEADERS,
          'Accept': '*/*',
        },
        body: JSON.stringify(requestBody), // Keep body for backward compatibility
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        // Intentar obtener detalles del error
        let errorMessage = 'Error al descargar archivos desde S3';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Si no podemos parsear la respuesta como JSON, usar el mensaje predeterminado
        }
        
        alert(`No se pudieron descargar los archivos: ${errorMessage}`);
        throw new Error(errorMessage);
      }
      
      // Primero intentar obtener la URL directamente de la respuesta JSON
      try {
        const contentType = response.headers.get('content-type');
        
        // Si la respuesta es JSON, podría contener una URL directa
        if (contentType && contentType.includes('application/json')) {
          const jsonResponse = await response.json();
          
          // Buscar la URL en varias ubicaciones posibles de la respuesta
          let downloadUrl = null;
          
          if (jsonResponse.url) {
            downloadUrl = jsonResponse.url;
          } else if (jsonResponse.body) {
            try {
              // A veces el body es un string JSON
              const parsedBody = typeof jsonResponse.body === 'string' 
                ? JSON.parse(jsonResponse.body) 
                : jsonResponse.body;
                
              if (parsedBody.url) {
                downloadUrl = parsedBody.url;
              }
            } catch (e) {
              // Si no podemos parsear el body múltiple, continuar con el método del blob
            }
          }
          
          if (downloadUrl) {
            // Abrir la URL en una nueva pestaña o usar un iframe para descargar
            window.open(downloadUrl, '_blank');
            
            logger.info('Archivos descargados exitosamente mediante URL directa:', fileNames);
            return { success: true, count: fileNames.length };
          }
        }
      } catch (jsonError) {
        // Continuar con el método de blob si hay un error procesando JSON
      }
      
      // Si no se pudo obtener una URL directa, proceder con el método del blob
      
      // For multiple files, we expect a ZIP file
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('Archivo ZIP vacío o archivos no disponibles');
      }
      
      // Create a download link and click it
      const downloadUrl = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      // Determinar el nombre del archivo descargado
      const downloadFileName = fileNames.length === 1 
        ? fileNames[0] 
        : `archivos_${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      
      a.download = downloadFileName;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      }, 0);
      
      logger.info('Archivos descargados exitosamente:', fileNames);
      
      return { success: true, count: fileNames.length };
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      if (fetchError.name === 'AbortError') {
        alert('La descarga ha tardado demasiado tiempo y se ha cancelado. Intente nuevamente.');
      }
      
      throw fetchError;
    }
  } catch (error) {
    logger.error('Error downloading files from S3:', error);
    alert(`Error al descargar los archivos: ${error.message}`);
    throw error;
  }
} 